import { useState, useCallback, useEffect, useMemo } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import { useGetLeadCitiesQuery } from "../../../redux/services/crmApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import {
  selectCrmLeadsFilters,
  clearCrmLeadsDestinationCity,
  setCrmLeadsDestinationCity,
  selectCommunicationsFilters,
  clearCommunicationsCity,
  setCommunicationsCity,
  clearCommunicationsUniversity,
  clearCrmLeadsUniversity,
} from "../../../redux/slices/filterSlice";
import {
  setPmsCity,
  clearPmsCity,
  clearPmsUniversity,
  selectPmsPropertiesFilters,
} from "../../../redux/slices/pmsFilterSlice";
import {
  selectTasksFilters,
  setTasksCityIds,
} from "../../../redux/slices/tasksFilterSlice";
import { useDispatch, useSelector } from "react-redux";

interface City {
  id: string;
  name: string;
}

const CityFilter = ({
  onClose,
  isCommunication = false,
  isMultiSelect = false,
  isPms = false,
  isCrmDestinationCity = false,
  isTasks = false,
}: {
  onClose: () => void;
  isCommunication?: boolean;
  isMultiSelect?: boolean;
  isPms?: boolean;
  isCrmDestinationCity?: boolean;
  isTasks?: boolean;
}) => {
  const leadsFilters = useSelector(selectCrmLeadsFilters);
  const communicationsFilters = useSelector(selectCommunicationsFilters);
  const pmsPropertiesFilters = useSelector(selectPmsPropertiesFilters);
  const tasksFilters = useSelector(selectTasksFilters);

  const destinationCountry = isTasks
    ? tasksFilters?.countryIds
    : isPms
    ? pmsPropertiesFilters?.destinationCountry
    : isCommunication
    ? communicationsFilters?.destinationCountry
    : leadsFilters?.destinationCountry;

  const selectedCity = isTasks
    ? tasksFilters?.cityIds
    : isPms
    ? pmsPropertiesFilters?.city
    : isCommunication
    ? communicationsFilters?.city
    : leadsFilters?.destinationCity;

  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCities, setFilteredCities] = useState<
    SearchableDropdownOption[]
  >([]);

  const dispatch = useDispatch();

  const countryIdsForCities = isTasks
    ? (tasksFilters?.countryIds?.value as string[]) ?? []
    : isPms
      ? pmsPropertiesFilters?.destinationCountry?.id
        ? [String(pmsPropertiesFilters.destinationCountry.id)]
        : []
      : (destinationCountry.value as string[]);

  const {
    data: cities,
    isLoading,
    isFetching,
  } = useGetLeadCitiesQuery(countryIdsForCities, {
    skip: countryIdsForCities.length === 0,
  });

  const transformedCities = useMemo(
    () =>
      cities?.map((city: City) => ({
        value: city.id,
        label: city.name,
      })) || [],
    [cities]
  );

  // Helper to get single value from selectedCity (handles both string and array formats)
  const getSingleValue = (val: string | string[] | undefined): string => {
    if (!val) return "";
    if (Array.isArray(val)) return val[0] || "";
    return val;
  };

  // Single select state
  const [selectedCityId, setSelectedCityId] = useState<{
    value: string;
    displayValue: string;
  }>({
    value: !isMultiSelect ? getSingleValue(selectedCity.value as string | string[]) : "",
    displayValue: !isMultiSelect ? getSingleValue(selectedCity.displayValue as string | string[]) : "",
  });

  // Multi select state
  const [selectedCityIds, setSelectedCityIds] = useState<
    Array<{ value: string; displayValue: string }>
  >(
    isMultiSelect && Array.isArray(selectedCity.value)
      ? (selectedCity.value as string[]).map((id, index) => ({
          value: id,
          displayValue: (selectedCity.displayValue as string[])[index] || "",
        }))
      : []
  );

  // Handle search functionality
  const handleSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setFilteredCities(transformedCities);
        return;
      }

      const filtered = transformedCities.filter(
        (city: SearchableDropdownOption) =>
          city.label?.toString().toLowerCase().includes(query.toLowerCase())
      );
      setFilteredCities(filtered);
    },
    [transformedCities]
  );

  // Debounced search
  useEffect(() => {
    handleSearch(searchQuery);
  }, [searchQuery, handleSearch]);

  // Initialize filtered countries
  useEffect(() => {
    setFilteredCities(transformedCities);
  }, [transformedCities]);

  const handleCitySelect = (cityId: string, cityLabel: string) => {
    if (isMultiSelect) {
      setSelectedCityIds((prevCities) => {
        const isAlreadySelected = prevCities.some((c) => c.value === cityId);
        if (isAlreadySelected) {
          return prevCities.filter((c) => c.value !== cityId);
        }
        return [
          ...prevCities,
          {
            value: cityId,
            displayValue: cityLabel,
          },
        ];
      });
    } else {
      if (cityId === selectedCityId.value) {
        setSelectedCityId({ value: "", displayValue: "" });
        dispatch(
          isCommunication
            ? clearCommunicationsCity()
            : clearCrmLeadsDestinationCity()
        );
        onClose();
      } else {
        setSelectedCityId({ value: cityId, displayValue: cityLabel });
      }
    }
  };

  const handleCancel = () => {
    if (isMultiSelect) {
      setSelectedCityIds([]);
    } else {
      setSelectedCityId({ value: "", displayValue: "" });
    }
    setSearchQuery("");
    onClose();
  };

  const handleApply = () => {
    if (isMultiSelect) {
      const cityValues = selectedCityIds.map((c) => c.value);
      const cityDisplayValues = selectedCityIds.map((c) => c.displayValue);

      // Multi-select is only for communications
      if (isCommunication) {
        dispatch(
          setCommunicationsCity({
            value: cityValues,
            displayValue: cityDisplayValues,
          })
        );
        dispatch(clearCommunicationsUniversity());
      }
      if (isCrmDestinationCity) {
        dispatch(
          setCrmLeadsDestinationCity({
            value: cityValues,
            displayValue: cityDisplayValues,
          })
        );
        dispatch(clearCrmLeadsUniversity());
      }
      if (isTasks) {
        dispatch(
          setTasksCityIds({
            value: cityValues,
            displayValue: cityDisplayValues,
          })
        );
      }
    } else {
      if (selectedCityId.value === getSingleValue(selectedCity.value as string | string[])) {
        return;
      }

      if (isPms) {
        dispatch(
          setPmsCity({
            id: selectedCityId.value ? Number(selectedCityId.value) : null,
            value: selectedCityId.value as string,
            displayValue: selectedCityId.displayValue as string,
          })
        );
        dispatch(clearPmsUniversity());
      } else if (isCommunication) {
        dispatch(
          setCommunicationsCity({
            value: [selectedCityId.value as string],
            displayValue: [selectedCityId.displayValue as string],
          })
        );
        dispatch(clearCommunicationsUniversity());
      } 
      // else {
      //   dispatch(
      //     setCrmLeadsDestinationCity({
      //       value: selectedCityId.value as string,
      //       displayValue: selectedCityId.displayValue as string,
      //     })
      //   );
      //   dispatch(clearCrmLeadsUniversity());
      // }
    }
    onClose();
  };

  return (
    <div className="flex flex-col h-full max-h-[90vh]">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk shrink-0">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search cities"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Multi-Select: Selected Count */}
      {isMultiSelect && (
        <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm px-3 py-1 shrink-0">
          SELECTED ({selectedCityIds?.length ?? 0})
        </span>
      )}

      {/* Multi-Select: Selected Cities Display */}
      {isMultiSelect && selectedCityIds.length > 0 && (
        <div className="flex flex-col divide-y divide-border-gray dark:divide-border-gray-dk max-h-[300px] overflow-y-auto shrink-0">
          {selectedCityIds.map((city) => (
            <div
              key={city.value}
              onClick={() => {
                handleCitySelect(city.value, city.displayValue);
              }}
              className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                  {city.displayValue}
                </span>
              </div>
              <div className="flex items-center justify-center w-3 h-3 border rounded border-verba-blue dark:border-verba-blue-dk bg-verba-blue dark:bg-verba-blue-dk">
                <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Single-Select: Selected City Display */}
      {!isMultiSelect && selectedCityId.value && (
        <div
          className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk shrink-0 cursor-pointer"
          onClick={() => setSelectedCityId({ value: "", displayValue: "" })}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {
                transformedCities.find(
                  (c: SearchableDropdownOption) =>
                    c.value === selectedCityId.value
                )?.label
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Countries List */}
      <div className="flex flex-col flex-1 min-h-0">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Cities
        </div>

        <div className="overflow-y-auto flex-1">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading cities..." : "Fetching cities..."}
              </div>
            </div>
          ) : filteredCities.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No cities found" : "No cities available"}
              </div>
            </div>
          ) : (
            filteredCities
              ?.filter((city: SearchableDropdownOption) =>
                isMultiSelect
                  ? !selectedCityIds.some((c) => c.value === city.value)
                  : city.value !== selectedCityId.value
              )
              ?.map((city: SearchableDropdownOption) => (
                <div
                  key={city.value}
                  onClick={() =>
                    handleCitySelect(city.value, city.label as string)
                  }
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {city.label}
                    </div>
                  </div>
                  {isMultiSelect ? (
                    <div className="flex items-center justify-center w-3 h-3 border rounded border-border-gray dark:border-border-gray-dk">
                      <div className="w-3 h-3 rounded-sm bg-transparent" />
                    </div>
                  ) : (
                    selectedCityId.value === city.value && (
                      <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                    )
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2 shrink-0 pt-4">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={handleCancel}
        />
        <Button
          innerText="Apply"
          disabled={
            isMultiSelect
              ? selectedCityIds.length === 0
              : selectedCityId.value === getSingleValue(selectedCity.value as string | string[])
          }
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CityFilter;
